const BASE = "/brochure";

export const BROCHURE_PDF = `${BASE}/Fit-Farm-Corporate-Profile.pdf`;

export const BROCHURE_PAGES = [
  "Cover & Training Services",
  "Beyond the Rabbit's Foot",
  "Team Building & Engagement",
  "Core Training Solutions 01–03",
  "Core Training Solutions 04–06",
  "Moments of Impact",
  "Trusted by Leading Brands",
].map((title, i) => ({ src: `${BASE}/pages/page-${i + 1}.jpg`, alt: `Corporate Profile page ${i + 1}: ${title}` }));

export const STATS = [
  { number: "12+", label: "Years of Experience" },
  { number: "150+", label: "Training Sessions" },
  { number: "10,000+", label: "Participants Impacted" },
  { number: "6", label: "Countries" },
];

export const STORY_IMAGES = {
  mascot: `${BASE}/story/mascot.jpg`,
  rabbit: `${BASE}/story/rabbit.jpg`,
  farm: `${BASE}/story/farm.jpg`,
};

export const RABBIT_PRINCIPLES = [
  { key: "aware", title: "Aware", lines: ["Stay curious.", "Notice change.", "Understand people and situations."] },
  { key: "adapt", title: "Adapt", lines: ["Respond quickly.", "Be flexible.", "Turn challenges into opportunities."] },
  { key: "connect", title: "Connect", lines: ["Build trust.", "Collaborate.", "Grow stronger together."] },
  { key: "move", title: "Move", lines: ["Take action.", "Be accountable.", "Keep progressing."] },
  { key: "grow", title: "Grow", lines: ["Develop continuously.", "Unlock potential.", "Create lasting impact."] },
];

export const CORE_SOLUTIONS = [
  {
    value: "team-building",
    title: "Team Building & Engagement",
    tagline: "Stronger People. Brighter Teams.",
    slogan: "Teams go further together.",
    desc: "We design engaging and experiential team building programmes that go beyond fun. Our activities create meaningful connections, build trust and strengthen collaboration, helping teams perform better together, back at work and beyond.",
    outcomes: [
      ["Build Trust", "Create stronger relationships and psychological safety"],
      ["Improve Collaboration", "Break silos and work more effectively together"],
      ["Boost Engagement", "Increase motivation, energy and team pride"],
      ["Develop Problem Solving", "Tackle challenges creatively as a team"],
      ["Stronger Team Performance", "Turn learning into real workplace results"],
    ],
  },
  {
    value: "leadership",
    title: "Leadership Development",
    tagline: "Better Leaders. Brighter Tomorrows.",
    slogan: "People develop people.",
    desc: "We develop confident, capable and accountable leaders at all levels. Through experiential learning, practical tools and real world application, we help individuals lead with greater self-awareness, influence and impact.",
    outcomes: [
      ["Greater Self-Awareness", "Understand your strengths, blind spots and leadership style"],
      ["Stronger Leadership Capability", "Build the skills and mindset to lead with confidence"],
      ["Better Decision Making", "Make clearer, more informed and courageous decisions"],
      ["Influence & Communication", "Communicate with clarity and inspire action"],
      ["Lasting Organisational Impact", "Create stronger teams and a healthier leadership culture"],
    ],
  },
  {
    value: "sales-influence",
    title: "Sales & Influence",
    tagline: "Conversations That Create Opportunities.",
    slogan: "Influence creates opportunities.",
    desc: "We help individuals and teams strengthen their influence, communication and consultative selling skills. Our programmes focus on real world application, enabling participants to build stronger relationships, create value and achieve better results.",
    outcomes: [
      ["Clearer Communication", "Communicate with confidence and build stronger rapport"],
      ["Stronger Influence", "Persuade ethically and create greater buy-in"],
      ["Consultative Selling Skills", "Understand needs, create value and offer relevant solutions"],
      ["Improved Sales Performance", "Achieve measurable results and grow business opportunities"],
      ["Lasting Relationships", "Build trust and long term partnerships with clients"],
    ],
  },
  {
    value: "mental-resilience",
    title: "Mental Resilience & Stress Management",
    tagline: "Calmer Minds. Stronger Performance.",
    slogan: "Calmer people. Stronger teams.",
    desc: "We help individuals and teams build resilience, manage stress and create healthier and more positive workplaces through practical tools, real world strategies and engaging experiences.",
    outcomes: [
      ["Build Resilience", "Develop mental strength and adaptability"],
      ["Manage Stress", "Learn practical techniques to handle pressure effectively"],
      ["Healthier Mindset", "Build positivity, emotional balance and greater focus"],
      ["Improved Performance", "Stay motivated, productive and perform at your best"],
    ],
  },
  {
    value: "communication-eq",
    title: "Communication & Emotional Intelligence (EQ)",
    tagline: "Better Conversations. Stronger Relationships.",
    slogan: "Real conversations. Real connections.",
    desc: "We help individuals and teams enhance communication, strengthen relationships and improve workplace interactions through greater self-awareness, empathy and emotional intelligence.",
    outcomes: [
      ["Clearer Communication", "Express ideas with clarity and impact"],
      ["Stronger Relationships", "Build trust, respect and collaboration"],
      ["Higher Empathy", "Understand and connect with others more effectively"],
      ["Improved Interactions", "Create a more positive and productive workplace culture"],
    ],
  },
  {
    value: "health-wellness",
    title: "Health & Wellness",
    tagline: "Healthy People. Happier Teams.",
    slogan: "Healthy people. Brighter tomorrows.",
    desc: "We promote a healthy, active and balanced lifestyle at work and beyond through engaging wellness programmes that energise individuals and strengthen teams.",
    outcomes: [
      ["Active Lifestyle", "Encourage movement and healthier habits"],
      ["Physical Wellness", "Improve overall health and energy levels"],
      ["Balanced Living", "Support work life harmony and sustainable habits"],
      ["Stronger Teams", "Build healthier, happier and more engaged teams"],
    ],
  },
].map((s, i) => ({ ...s, number: String(i + 1).padStart(2, "0"), image: `${BASE}/solutions/solution-${i + 1}.jpg` }));

export const MOMENTS = Array.from({ length: 15 }, (_, i) => ({
  src: `${BASE}/moments/moment-${String(i + 1).padStart(2, "0")}.jpg`,
  alt: `Fit Farm moment of impact ${i + 1}`,
}));

const LOGO_NAMES = {
  ikm: "Institut Koperasi Malaysia", mmc: "MMC", singtel: "Singtel", sefar: "SEFAR", tmaker: "TMaker", als: "ALS",
  suezcap: "Suezcap", oncocare: "OncoCare Tamarind Health", sph: "SPH", asia: "Asia", shapadu: "Shapadu", crasia: "CR Asia",
  astachem: "Astachem Specialty Chemical", airselangor: "Air Selangor", electrolux: "Electrolux", fbnutrition: "F&B Nutrition",
  hapaglloyd: "Hapag-Lloyd", pfizer: "Pfizer", bankrakyat: "Bank Rakyat", bjak: "BJAK", tohtonku: "Tohtonku", wyeth: "Wyeth",
  nestle: "Nestlé", bankmuamalat: "Bank Muamalat", silterra: "SilTerra", reckitt: "Reckitt Benckiser", airasia: "AirAsia",
  rhb: "RHB", asterspring: "Aster Spring", pos: "Pos Malaysia", abbott: "Abbott", elika: "Elika", ntw: "National Training Week",
  kenvue: "Kenvue", jnj: "Johnson & Johnson",
};

export const BRAND_LOGOS = Object.entries(LOGO_NAMES).map(([key, name]) => ({ key, name, src: `${BASE}/logos/${key}.png` }));
